import type { User } from '../../types/index.js';

/**
 * Spec 247 — how the signed-in person is named, once, in one format.
 *
 * The 2026-09-27 review found this app showing the account's email TWICE over
 * four lines. The cause was `user.name`: with no display name on the Orbit
 * profile it falls back to the email, so "name" and "email" rendered as the
 * same string one above the other. The review also found one person spelled
 * three ways across the seven hub apps.
 *
 * Kridt's rule: the display name; the email stands in ONLY when there is no
 * name, and never appears twice.
 */
export function displayName(user: Pick<User, 'name' | 'email'>): string {
  const name = (user.name ?? '').trim();
  // A "name" that is really an address is the fallback, not a name.
  if (name && name !== user.email && !name.includes('@')) return name;
  return user.email;
}

/** Two initials for the neutral disc. "Ulrich Kronvold" → "UK"; "ulrich.k@x" → "UK". */
export function initials(name: string): string {
  const base = name.includes('@') ? name.split('@')[0]! : name;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const pair = parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : base.slice(0, 2);
  return pair.toUpperCase();
}

/**
 * The person's role in plain words. Most senior first, because a person can
 * hold several and the block shows one line.
 */
export function roleLabel(user: Pick<User, 'roles' | 'isAdmin' | 'isAppOwner' | 'isDeveloper'>): string {
  if (user.isAdmin || user.roles?.includes('orbit:admin')) return 'Admin';
  if (user.isAppOwner) return 'App owner';
  if (user.isDeveloper) return 'Developer';
  return 'Viewer';
}
